import React, { useEffect, useState, useCallback} from "react";
import { useParams } from "react-router-dom";
import ReactDatetime from "react-datetime";
import projectService from "../../services/api/projects-service";
import moment from "moment";
import { faRupeeSign, faDollarSign, faUserPlus, faUserTimes, faPlus, faUserCheck } from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Form,
  CardTitle,
  Container,
  Row,
  Col,
} from "reactstrap";
import DailyMembershipStatsService from "../../services/api/daily-membership-stats-service.js";
import StatsCard from "./StatsCard.js";
import ChartTabs from "./ChartTabs";
import Moment from "react-moment";

const RECORDS_PER_PAGE = "";

const INITIAL_FORM_STATE = {
  from_date: moment().startOf("day").toDate(),
  to_date: moment().endOf("day").toDate(),
  project_id: "",
};

const DailyMembershipStats = () => {
  const { id } = useParams();
  console.log("ID from URL:", id);
  const [dailyStats, setDailyStats] = useState([]);
  const [todayStats, setTodayStats] = useState({
    total_inr_sales_amount: 0,
    total_usd_sales_amount: 0,
    total_inr_sales : 0,
    total_usd_sales : 0,
    active_subscribers: 0,
    expired_subscribers: 0,
    newly_purchased: 0,
    renewed: 0,
    cancelled: 0,
  });
  const [formState, setFormState] = useState({
    ...INITIAL_FORM_STATE,
    project_id: id
  });
  const [activePage, setActivePage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [rangeFilter, setRangeFilter] = useState("day");

  const calculateDateRange = (range) => {
    const today = moment().endOf("day");
    let fromDate = moment().startOf("day");

    switch (range) {
      case "day":
        fromDate = moment().startOf("day");
        break;
      case "week":
        fromDate = moment().subtract(7, "days").startOf("day");
        break;
      case "month":
        fromDate = moment().subtract(1, "month").startOf("day");
        break;
      case "3months":
        fromDate = moment().subtract(3, "months").startOf("day");
        break;
      case "6months":
        fromDate = moment().subtract(6, "months").startOf("day");
        break;
      case "1year":
        fromDate = moment().subtract(1, "year").startOf("day");
        break;
      default:
        return;
    }

    setFormState((prev) => ({
      ...prev,
      from_date: fromDate.toDate(),
      to_date: today.toDate(),
    }));
  };

  // Ensure project_id is always set
  useEffect(() => {
    if (id && formState.project_id !== id) {
      setFormState(prev => ({ ...prev, project_id: id }));
    }
  }, [id, formState.project_id]);

  const handleFormChange = useCallback((field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value, project_id: id }));
  }, [id]);

  const handleFilterReset = useCallback(() => {
    setFormState({
      ...INITIAL_FORM_STATE,
      project_id: id, 
    });
    setActivePage(1);
    setRangeFilter("day");
  }, [id]);

  const fetchDailyStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters = { ...formState, project_id: id };
      console.log("Fetching data with filters:", filters);
  
      const result = await DailyMembershipStatsService.getDailyMembershipStats(
        filters,
        activePage,
        RECORDS_PER_PAGE
      );
  
      if (result?.status) {
        const stats = result.data.allDailyStats || [];
        setDailyStats(stats);

        // Aggregate total stats from the full range
        const aggregated = stats.reduce((acc, day) => {
          acc.total_inr_sales_amount += day.total_inr_sales_amount || 0;
          acc.total_usd_sales_amount += day.total_usd_sales_amount || 0;
          acc.total_inr_sales += day.total_inr_sales || 0;
          acc.total_usd_sales += day.total_usd_sales || 0;
          acc.newly_purchased += day.newly_purchased || 0;
          acc.renewed += day.renewed || 0;
          acc.cancelled += day.cancelled || 0;
          acc.expired_subscribers += day.expired_subscribers || 0;
          return acc;
        }, {
          total_inr_sales_amount: 0,
          total_usd_sales_amount: 0,
          total_inr_sales: 0,
          total_usd_sales: 0,
          newly_purchased: 0,
          renewed: 0,
          cancelled: 0,
          expired_subscribers: 0,
        });

        const toDateFormatted = moment(formState.to_date).format('YYYY-MM-DD');
        const exactMatchDay = stats.find(day =>
          moment(day.date).format('YYYY-MM-DD') === toDateFormatted
        );

        aggregated.active_subscribers = exactMatchDay?.active_subscribers || 0;
        // aggregated.expired_subscribers = exactMatchDay?.expired_subscribers || 0;


        setTodayStats(aggregated);
      }
    } catch (error) {
      console.error("Error fetching daily stats:", error);
    } finally {
      setIsLoading(false);
    }
  }, [formState, activePage, id]);
  

  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    try {
      const projectsData = await projectService.getAllProjects({ status: 1 });
      if (projectsData.status && projectsData.data) {
        setProjects(
          projectsData.data.map((proj) => ({
            value: proj.id,
            label: proj.name,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Fetch stats whenever formState changes or component mounts
  useEffect(() => {
    fetchDailyStats();
  }, [formState, activePage, fetchDailyStats]);

  const handleFilterSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setActivePage(1); // Reset to the first page when filters change
      fetchDailyStats();
    },
    [fetchDailyStats]
  );



  return (
    <div className="content">
      <Container>
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center justify-content-between">
                  <CardTitle tag="h4">Daily Membership Stats </CardTitle>
                  <div>
                    <Button
                      color="warning"
                      size="sm"
                      onClick={() => window.history.back()}
                    >
                      <i className="fa fa-arrow-left mr-1"></i> Back
                    </Button>
                  </div>
                </div>
                <CardTitle tag="h6" className="text-center">
                  Stats from <Moment format="DD-MM-YYYY">{formState.from_date}</Moment> to <Moment format="DD-MM-YYYY">{formState.to_date}</Moment>
                </CardTitle>

           

                <Row>
                <Col md="2">
                <StatsCard value={todayStats.total_inr_sales} label = {"INR Sales Count"} icon ={faPlus} color="info" />
              </Col>

              <Col md="2">
                <StatsCard value={todayStats.total_inr_sales_amount } label = {"INR Sales Amount"} icon ={faRupeeSign} color="info" />
              </Col>


              <Col md="2">
                <StatsCard value={todayStats.total_usd_sales} label = {"USD Sales Count"} icon ={faPlus} color="info"/>
              </Col>


              <Col md="2">
              <StatsCard value={todayStats.total_usd_sales_amount} label = {"USD Sales Amount"} icon ={faDollarSign} color="info"/>
              </Col>

              <Col md="2">
                <StatsCard value={todayStats.active_subscribers} label = {"Active Subscriber"} icon ={faUserPlus} color="info" />
              </Col>

              <Col md="2">
                <StatsCard value={todayStats.expired_subscribers} label = {"Expired Subscriber"} icon ={faUserTimes} color="info"/>
              </Col>
            </Row>

            <Row>
              
              <Col md="2">
                <StatsCard value={todayStats.newly_purchased} label = {"Membership Purchases Today"} icon ={faUserPlus} color="info"/>
              </Col>
              <Col md="2">
                <StatsCard value={todayStats.renewed} label = {"Membership Renewals Today"} icon ={faUserCheck} color="info"/>
              </Col>
              <Col md="2">
                <StatsCard value={todayStats.cancelled} label = {"Memberships Cancelled Today"} icon ={faUserTimes} color="info"/>
              </Col>
            </Row>

                <Form onSubmit={handleFilterSubmit}>
                  <Row className="d-flex align-items-center" >
                    <Col md="3">
                      <ReactDatetime
                        value={formState.from_date ? moment(formState.from_date) : null}
                        onChange={(date) => {
                          handleFormChange("from_date", moment(date).toDate());
                          setRangeFilter("");
                          }
                        }
                        inputProps={{
                          className: "form-control",
                          placeholder: "From Date",
                        }}
                      />
                    </Col>
                    <Col md="3">
                      <ReactDatetime
                        value={formState.to_date ? moment(formState.to_date) : null}
                        onChange={(date) => {
                          handleFormChange("to_date", moment(date).toDate());
                          setRangeFilter("");
                          }
                        }
                        inputProps={{
                          className: "form-control",
                          placeholder: "To Date",
                        }}
                      />
                    </Col>
                    <Col md="3">
                      <select
                        className="form-control"
                        value={rangeFilter}
                        onChange={(e) => {
                          const value = e.target.value;
                          setRangeFilter(value);
                          calculateDateRange(value);
                        }}
                      >
                        <option value="">Select Range</option>
                        <option value="day">Today</option>
                        <option value="week">Last 7 Days</option>
                        <option value="month">Last 1 Month</option>
                        <option value="3months">Last 3 Months</option>
                        <option value="6months">Last 6 Months</option>
                        <option value="1year">Last 1 Year</option>
                      </select>
                    </Col>
                    <Col md="3">
                      <Button
                        className="btn-round mr-2"
                        color="info"
                        type="submit"
                        disabled={isLoading}
                      >
                        {isLoading ? "Loading..." : "Search"}
                      </Button>
                      <Button
                        className="btn-round"
                        color="warning"
                        type="button"
                        onClick={handleFilterReset}
                      >
                        Reset
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardHeader>

              <CardBody>
            
               
                <Row>
                
                  <Col md="12">
                  <ChartTabs dailyStats={dailyStats} />
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DailyMembershipStats;